// src/pages/TownAdminApplicationPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { db } from '../lib/firebase';
import { collection, addDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { getTowns } from '../lib/db';
import toast from 'react-hot-toast';

const TownAdminApplicationPage = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [towns, setTowns] = useState([]);
  const [form, setForm] = useState({ townId: '', reason: '' });
  const [loading, setLoading] = useState(false);
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    getTowns().then(setTowns).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) { setChecking(false); return; }
    getDocs(query(
      collection(db, 'town_admin_applications'),
      where('userId', '==', user.uid),
      where('status', '==', 'pending')
    )).then(snap => {
      setAlreadyApplied(!snap.empty);
      setChecking(false);
    }).catch(() => setChecking(false));
  }, [user]);

  const handleSubmit = async () => {
    if (!form.townId) { toast.error('Please choose a town'); return; }
    if (!form.reason.trim()) { toast.error('Please tell us a little about why'); return; }
    setLoading(true);
    try {
      await addDoc(collection(db, 'town_admin_applications'), {
        userId: user.uid,
        displayName: profile?.displayName || user.displayName || '',
        email: user.email || '',
        townId: form.townId,
        townName: towns.find(t => t.id === form.townId)?.name || form.townId,
        reason: form.reason.trim(),
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      toast.success('Application sent — we\'ll be in touch soon');
      setTimeout(() => navigate('/support-your-town'), 1200);
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong — please try again');
    }
    setLoading(false);
  };

  if (checking) {
    return <div style={{ ...s.page, display: 'flex', justifyContent: 'center', paddingTop: 80 }}><div className="spinner" /></div>;
  }

  return (
    <div style={s.page}>
      <button onClick={() => navigate(-1)} style={s.back}>← Back</button>
      <h1 style={s.title}>Become a Town Admin</h1>
      <p style={s.subtitle}>
        Town Admins help keep one town's events, services and listings accurate and up to date. It's a trust-based role — every application is reviewed before approval, and more than one Town Admin per town is fine.
      </p>

      {alreadyApplied ? (
        <div style={s.card}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>Application already sent</div>
          <div style={{ fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.5 }}>
            You've already applied and it's waiting for review. We'll be in touch soon — no need to apply again.
          </div>
        </div>
      ) : (
        <>
          <div style={s.card}>
            <label style={s.label}>Which town? *</label>
            <select style={s.input} value={form.townId} onChange={e => setForm({ ...form, townId: e.target.value })}>
              <option value="">Select a town...</option>
              {towns.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>

            <label style={s.label}>Why would you like to help with this town? *</label>
            <textarea
              style={{ ...s.input, height: 90, resize: 'none' }}
              placeholder="e.g. I'm involved with local clubs and would like to help keep listings accurate and current"
              value={form.reason}
              onChange={e => setForm({ ...form, reason: e.target.value })}
            />
          </div>
          <button style={{ ...s.btn, opacity: loading ? 0.7 : 1 }} onClick={handleSubmit} disabled={loading}>
            {loading ? 'Sending...' : 'Send application →'}
          </button>
          <p style={s.note}>Applications are reviewed by hand — you'll hear back once it's been looked at.</p>
        </>
      )}
    </div>
  );
};

const s = {
  page: { padding: '24px 20px 40px', maxWidth: 430, margin: '0 auto', background: '#fff', minHeight: '100vh' },
  back: { background: 'none', border: 'none', color: '#1A7A4A', fontSize: 15, fontWeight: 600, cursor: 'pointer', marginBottom: 20, padding: 0 },
  title: { fontSize: 26, fontWeight: 800, color: '#2C342C', marginBottom: 8 },
  subtitle: { fontSize: 15, color: '#5E6F5E', marginBottom: 28, lineHeight: 1.5 },
  card: { background: '#F8FBF8', borderRadius: 16, padding: 20, marginBottom: 20 },
  label: { display: 'block', fontSize: 13, fontWeight: 700, color: '#2C342C', marginBottom: 6, marginTop: 16 },
  input: { width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #C8D9C9', fontSize: 15, fontFamily: 'Nunito, sans-serif', background: '#fff', boxSizing: 'border-box' },
  btn: { width: '100%', padding: '16px', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 800, cursor: 'pointer' },
  note: { fontSize: 12, color: '#5E6F5E', textAlign: 'center', marginTop: 16 },
};

export default TownAdminApplicationPage;
